import { isEmpty } from '@ember/utils'
import fillMonths from 'district-ui-client/utils/student-events/fill-months'
import type StudentEventsService from 'district-ui-client/services/student-events'
import type { NormalizedInterval } from 'date-fns'
import type { StatsScope } from 'district-ui-client/utils/student-events/stats-scope'

export interface StorylandsTotalsResponse {
  storylands_totals_summary_by_grade: {
    grade_position: number
    data: [{ category: 'total_count'; count: number }, { category: 'average_score'; average: number }]
  }[]
  storylands_totals_by_grade_by_month: {
    grade_position: number
    months: {
      month: string
      count: number
    }[]
  }[]
}

export interface StorylandsTotalsDataItem {
  grade_position: number
  summary: {
    total_count: number
    average_score: number
  }
  months: {
    month: string
    count: number
  }[]
}

export async function storylandsTotalsByGradeByMonth(
  this: StudentEventsService,
  product: LegacyProductRe,
  statsScope: StatsScope,
  period: NormalizedInterval,
): Promise<Nullable<StorylandsTotalsDataItem[]>> {
  if (isEmpty(statsScope.ids)) return Promise.resolve(null)

  const url = this.buildUrl('storylands_totals_by_grade_by_month', product, statsScope)
  const query = this.buildQuery(statsScope, period)

  const data = await this.fetchData<StorylandsTotalsResponse>(url, query)
  if (!data) return null

  const { storylands_totals_summary_by_grade: summaryData, storylands_totals_by_grade_by_month: monthData } = data
  if (monthData.length === 0) return null

  return groupByGrade(summaryData, monthData, period)
}

function groupByGrade(
  summaryData: StorylandsTotalsResponse['storylands_totals_summary_by_grade'],
  monthData: StorylandsTotalsResponse['storylands_totals_by_grade_by_month'],
  period: NormalizedInterval,
) {
  const sortedSummary = [...summaryData].sort((a, b) => a.grade_position - b.grade_position)

  return sortedSummary
    .map((summary) => {
      const gradeMonthData = monthData.find((month) => month.grade_position === summary.grade_position)
      if (!gradeMonthData) return null

      return {
        grade_position: summary.grade_position,
        summary: {
          total_count: summary.data.find((summaryItem) => summaryItem.category === 'total_count')?.count ?? 0,
          average_score: summary.data.find((summaryItem) => summaryItem.category === 'average_score')?.average ?? 0,
        },
        months: fillMonths(gradeMonthData.months, period, {
          count: 0,
        }),
      }
    })
    .filter((data) => data !== null)
}
